import { useTranslation } from 'react-i18next';
import { Plane, MapPin, Users, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { DisruptionDetail } from '@/lib/disruptions/types';
import { formatDateTime } from '../labels';
import { Link } from 'react-router';

interface DisruptionFlightMatrixProps {
  detail: DisruptionDetail;
}

export function DisruptionFlightMatrix({ detail }: DisruptionFlightMatrixProps) {
  const { t } = useTranslation('tower');
  const cases = detail.cases;

  return (
    <Card className="border-border bg-card shadow-sm" data-testid="flight-matrix">
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-sm font-bold text-foreground">
            <Plane className="h-4 w-4 text-primary" />
            <span>{t('alt.matrix.title', { count: cases.length })}</span>
          </CardTitle>

          {/* Scope details */}
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
              {t('alt.matrix.airports')}{' '}
              <b className="text-foreground">{detail.scope.airports.join(', ')}</b>
            </span>
            {detail.scope.window?.fromUtc ? (
              <span>
                {t('alt.matrix.window', { from: formatDateTime(detail.scope.window.fromUtc) })}
                {detail.scope.window?.toUtc ? ` ➔ ${formatDateTime(detail.scope.window.toUtc)}` : ''}
              </span>
            ) : null}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4 pt-2">
        {cases.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">
            {t('alt.matrix.empty')}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {cases.map((c) => (
              <Link
                key={c.caseUrn}
                to={`/cases/${encodeURIComponent(c.caseUrn)}`}
                className="group flex flex-col justify-between rounded-xl border border-border/80 bg-muted/20 p-3 transition-all duration-200 hover:border-primary/50 hover:bg-muted/40 hover:shadow-xs"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 font-mono text-xs font-bold text-primary">
                      {c.flightNumber ?? c.airportCode}
                    </span>
                    <div>
                      <p className="text-xs font-bold text-foreground group-hover:text-primary transition-colors">
                        {c.flightNumber
                          ? t('alt.matrix.flight', { number: c.flightNumber })
                          : t('alt.matrix.case', { airport: c.airportCode })}
                      </p>
                      {c.origin && c.destination ? (
                        <p className="text-[11px] font-medium text-muted-foreground">
                          {c.origin} ➔ {c.destination}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <Badge variant="neutral" className="text-[10px] uppercase tracking-wider font-semibold">
                    {c.status}
                  </Badge>
                </div>

                <div className="mt-3 pt-2 border-t border-border/40 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1 font-semibold text-foreground">
                    <Users className="h-3.5 w-3.5 text-muted-foreground" />
                    {t('alt.matrix.pax', { count: c.passengerCount })}
                  </span>

                  {c.departureAt ? (
                    <span className="text-[11px]">
                      {new Date(c.departureAt).toLocaleDateString(undefined, { 
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  ) : null}

                  <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 text-primary transition-opacity" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
